import React, { useCallback, useState, useMemo, cloneElement, ReactElement } from "react";
import { Modal, Button } from "semantic-ui-react";
import { useAppSelector, useAppDispatch } from "store";
import { Other } from "simplydo/interfaces";
import actions from "actions";
import util from "utils/utils";

type IdeaLikeHandlerProps = {
  idea: Other.IIdea;
  trigger: ReactElement;
  onLikeIdea: (ideaId: string, addingLike: boolean) => void;
  challenge: Other.IChallenge;
};

const IdeaLikeHandler = ({ idea, onLikeIdea, challenge, trigger }: IdeaLikeHandlerProps) => {
  const user: Other.IUserMe = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const ideaId = idea._id;
  const hasVotedForIdea = idea.votedFor;

  const hasSeenIdeaLikeInformationModalThisSession = useAppSelector(
    (state) => state.organisations.hasSeenIdeaLikeInformationModalThisSession,
  );
  const [modalOpen, setModalOpen] = useState(false);

  const shouldShowLikeNotificationModal = useMemo(
    () => !util.ideaMeetsChallengeVisibilityLimits(idea, challenge) && util.canViewEntireChallenge(user, challenge),
    [challenge, user, idea],
  );

  const onClickLikeButton = useCallback(() => {
    if (shouldShowLikeNotificationModal && !hasSeenIdeaLikeInformationModalThisSession && !hasVotedForIdea) {
      setModalOpen(true);
    } else {
      onLikeIdea(ideaId, !hasVotedForIdea);
    }
  }, [
    shouldShowLikeNotificationModal,
    hasSeenIdeaLikeInformationModalThisSession,
    onLikeIdea,
    ideaId,
    hasVotedForIdea,
  ]);

  const onClickModalLikeButton = useCallback(() => {
    dispatch(actions.organisations.setHasSeenIdeaLikeInformationModalThisSession(true));
    onLikeIdea(ideaId, !hasVotedForIdea);
    setModalOpen(false);
  }, [dispatch, onLikeIdea, ideaId, hasVotedForIdea]);

  const onCloseModal = useCallback(() => {
    dispatch(actions.organisations.setHasSeenIdeaLikeInformationModalThisSession(true));
    setModalOpen(false);
  }, [dispatch]);

  return (
    <>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      >
        <Modal.Header>Idea Like Information</Modal.Header>
        <Modal.Content>
          When you "like" an idea in this challenge, the author(s) of the idea will be notified via email and/or push
          notification. Your name will be visible to the author(s) of the idea, and they will be able to see that you
          liked their idea. You are seeing this message because you have special permission to view ideas in this
          challenge, and this challenge only allows{" "}
          {challenge.ideaVisibilityLimits?.length
            ? `users to see ideas that are ${challenge.ideaVisibilityLimits.join("/")}`
            : "people with special permissions to see ideas"}
          .
        </Modal.Content>
        <Modal.Actions>
          <Button content="Cancel and don't like idea" onClick={onCloseModal} />
          <Button
            content="I understand and still want to like this idea"
            onClick={onClickModalLikeButton}
            primary
            icon="thumbs up"
          />
        </Modal.Actions>
      </Modal>

      {cloneElement(trigger, { onClick: onClickLikeButton })}
    </>
  );
};

export default IdeaLikeHandler;
